import type { MetaDescriptor } from 'react-router';
import { parseURL } from '@archipro-website/top-navigation';
import { NON_FILTER_KEYS } from './no-index-product-category';
import { getLocation } from '~/modules/search/util/filter-url-helpers';
import { getCountryCodeByName } from '~/modules/location/util';
import type { AddressCountry } from '@archipro-website/localisation';
import { noIndexNoFollow } from '@modules/seo/util/no-index-no-follow';
import { hasSortParam } from './has-sort-param';

/**
 * Index page for no filter or one location filter professional category pages.
 * `forceNoIndex` lets callers suppress indexing of otherwise-indexable pages using the
 * backend combo-indexability verdict (no unique SEO content or not enough supply),
 * avoiding duplicate-content / doorway-page risk.
 *
 * Non-indexable pages get `noindex` only (never `nofollow`) so link equity keeps
 * flowing through filter pages to professional profiles.
 * @param metaDescriptors
 * @param url
 * @param forceNoIndex
 * @returns
 */
export const noIndexProfessionalCategory = (
    metaDescriptors: MetaDescriptor[],
    url: string,
    forceNoIndex = false,
): MetaDescriptor[] => {
    if (!url.startsWith('/professionals')) return metaDescriptors;

    const { indexPage } = isLocationIndexPage(url);

    return noIndexNoFollow(
        true,
        false,
        indexPage && !forceNoIndex,
        metaDescriptors,
    );
};

export const isLocationIndexPage = (
    url: string,
): { indexPage: boolean; country: AddressCountry | null } => {
    const { params } = parseURL(url);
    const filterKeys = Object.keys(params).filter(
        (key) => !NON_FILTER_KEYS.includes(key),
    );
    const location = getLocation(params);
    const country = getCountryCodeByName(location?.country);
    const indexPage =
        !hasSortParam(params) &&
        (!filterKeys.length ||
            (filterKeys.length === 1 && filterKeys[0] === 'location'));
    return { indexPage: !!indexPage, country };
};
